/**
 * Fingerprints: a short, stable hash of what a node says.
 *
 * Pure and synchronous (no node:crypto, no WebCrypto) so the review panel
 * can compute one in the browser and the backend can verify it. Not a
 * security primitive — it detects "this text changed under the patch", and
 * a 64-bit non-cryptographic hash is ample for that.
 *
 * Assembly-filled fields (auto section numbers, paths, reference display
 * text, attachment-list entries) and identity/annotation fields are
 * excluded, so a node fingerprints the same in a compiled and a resolved
 * document.
 *
 * Versions (the prefix):
 *
 *  - `f2:` (current) — a value node bound to a form field (`sourceKey`)
 *    fingerprints as its field key (`{{key}}`), not its text: field
 *    references in adapted text are live (ADR-0102 addendum), so a changed
 *    form value is not a conflict; changed standard text around it still is.
 *    Value nodes without a `sourceKey` keep their text.
 *    An option list bound to a field (`sourceKey`) ignores its tick state
 *    in `f2` (the choice is a form value); its option text still counts.
 *    Stored `f2:` anchors made before option lists carried a `sourceKey`
 *    (tick state included) still match while the ticks are unchanged.
 *  - `f1:` — value text included. Logs written before f2 carry these;
 *    `fingerprintMatches` recomputes the old way for them, so they keep
 *    replaying exactly as before.
 */
import { canonicalJson } from '../document/revision.js';
export const FINGERPRINT_VERSION = 'f2';
const FINGERPRINT_PREFIX = 'f2:';
const LEGACY_PREFIX = 'f1:';
/** cyrb53-style 2×32-bit string hash → 16 hex chars. */
export function hashString(input) {
    let h1 = 0xdeadbeef;
    let h2 = 0x41c6ce57;
    for (let i = 0; i < input.length; i++) {
        const ch = input.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
    h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
    h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return (h2 >>> 0).toString(16).padStart(8, '0') + (h1 >>> 0).toString(16).padStart(8, '0');
}
const stripInline = (node, mode) => {
    switch (node.type) {
        case 'value':
            return mode !== 'f1' && node.sourceKey != null ? { type: 'value', ref: `{{${node.sourceKey}}}` } : node;
        case 'sectionRef':
        case 'attachmentRef':
        case 'documentRef': {
            const { text: _text, ...rest } = node;
            return rest;
        }
        case 'strong':
        case 'emphasis':
        case 'label':
            return { ...node, content: node.content.map((c) => stripInline(c, mode)) };
        default:
            return node;
    }
};
/** Review ghosts (removed nodes kept for track-changes) are not content. */
const stripList = (list, mode) => list.filter((n) => n.edit?.change !== 'removed').map((n) => stripBlock(n, mode));
const stripOption = (item, list, mode) => {
    const { edit: _e, checked, ...rest } = item;
    return {
        ...rest,
        ...(mode === 'f2' && (list.sourceKey ?? item.sourceKey) != null ? {} : { checked }),
        content: item.content.map((c) => stripInline(c, mode)),
        ...(item.children ? { children: stripList(item.children, mode) } : {}),
    };
};
const stripColumn = (column, mode) => {
    const { edit: _e, ...rest } = column;
    return rest.header ? { ...rest, header: rest.header.map((c) => stripInline(c, mode)) } : rest;
};
const stripRow = (item, mode) => ({
    term: item.term.map((c) => stripInline(c, mode)),
    description: stripList(item.description, mode),
});
/** The node minus everything assembly or annotation adds. */
function stripBlock(node, mode) {
    const si = (c) => stripInline(c, mode);
    const sl = (l) => stripList(l, mode);
    // A lock is policy, not wording: (un)locking a node never makes an edit on it stale.
    const { instanceId: _i, edit: _e, locked: _l, ...rest } = node;
    const n = rest;
    switch (n.type) {
        case 'section': {
            const { path: _p, number, ...section } = n;
            return {
                ...section,
                ...(n.numbering === 'fixed' && number != null ? { number } : {}),
                content: sl(n.content),
            };
        }
        case 'heading':
        case 'paragraph':
            return { ...n, content: n.content.map(si) };
        case 'notice':
        case 'group':
            return { ...n, content: sl(n.content) };
        case 'list':
            return { ...n, items: n.items.map((i) => ({ content: sl(i.content) })) };
        case 'optionList': {
            const { sourceKey: _k, ...list } = n;
            return { ...list, items: n.items.map((i) => stripOption(i, n, mode)) };
        }
        case 'table': {
            const { captionEdit: _c, ...table } = n;
            return {
                ...table,
                columns: n.columns.map((c) => stripColumn(c, mode)),
                rows: n.rows.map((r) => ({ ...r, cells: r.cells.map((c) => ({ ...c, content: sl(c.content) })) })),
                ...(n.caption ? { caption: n.caption.map(si) } : {}),
            };
        }
        case 'detailList':
            return {
                ...n,
                items: n.items.filter((i) => i.edit?.change !== 'removed').map((i) => stripRow(i, mode)),
            };
        case 'signature':
            return n.statement ? { ...n, statement: n.statement.map(si) } : n;
        case 'attachmentList': {
            const { entries: _entries, ...list } = n;
            return list;
        }
        default:
            return n;
    }
}
/**
 * Fingerprint of a node as a patch target.
 *
 * A section fingerprints SHALLOW (ref, title, numbering): a clause is
 * identified by what it is called, and a value changing deep inside a
 * removed or retitled clause must not make the patch conflict. Every other
 * node fingerprints its full subtree, so a value change in an edited
 * paragraph does.
 */
export function fingerprint(node) {
    return FINGERPRINT_PREFIX + nodeHash(node, 'f2');
}
/** The pre-f2 fingerprint (value text included) — compat for stored `f1:` anchors. */
export function fingerprintV1(node) {
    return LEGACY_PREFIX + nodeHash(node, 'f1');
}
function nodeHash(node, mode) {
    if (node.type === 'section') {
        const shallow = {
            type: 'section',
            ref: node.ref,
            title: node.title ?? null,
            numbering: node.numbering ?? 'auto',
            number: node.numbering === 'fixed' ? (node.number ?? null) : null,
        };
        return hashString(canonicalJson(shallow));
    }
    return hashString(canonicalJson(stripBlock(node, mode)));
}
/**
 * Fingerprint of a whole block instance: the shape of its top-level output
 * (node types + section refs), not its text.
 */
export function fingerprintInstance(nodes) {
    return FINGERPRINT_PREFIX + instanceHash(nodes);
}
const instanceHash = (nodes) => hashString(canonicalJson({ type: 'instance', shape: nodes.map((n) => (n.type === 'section' ? `section:${n.ref}` : n.type)) }));
/**
 * Whether a stored fingerprint still describes a node: `f2:` against the
 * current fingerprint, `f1:` recomputed the old way (compat). Anything
 * else never matches.
 */
export function fingerprintMatches(node, stored) {
    if (stored.startsWith(LEGACY_PREFIX))
        return stored === fingerprintV1(node);
    if (stored === fingerprint(node))
        return true;
    return hasBoundOptionList(node) && stored === FINGERPRINT_PREFIX + nodeHash(node, 'f2c');
}
const hasBoundOptionList = (node) => {
    const visit = (n) => {
        if (n.type === 'optionList' && n.sourceKey != null)
            return true;
        switch (n.type) {
            case 'section':
            case 'notice':
            case 'group':
                return n.content.some(visit);
            case 'list':
                return n.items.some((i) => i.content.some(visit));
            case 'optionList':
                return n.items.some((i) => (i.children ?? []).some(visit));
            case 'table':
                return n.rows.some((r) => r.cells.some((c) => c.content.some(visit)));
            case 'detailList':
                return n.items.some((i) => i.description.some(visit));
            default:
                return false;
        }
    };
    return node.type !== 'section' && visit(node);
};
const partShape = (owner, part, mode) => {
    switch (part.kind) {
        case 'row': {
            if (owner.type !== 'detailList')
                return undefined;
            const item = owner.items[part.index];
            return item ? { type: 'detailRow', ...stripRow(item, mode) } : undefined;
        }
        case 'option': {
            if (owner.type !== 'optionList')
                return undefined;
            const item = owner.items[part.index];
            // The label only: ticks are the form's, children are nodes of their own.
            return item ? { type: 'optionLabel', code: item.code ?? null, content: item.content.map((c) => stripInline(c, mode)) } : undefined;
        }
        case 'columnHeader': {
            if (owner.type !== 'table')
                return undefined;
            const column = owner.columns[part.index];
            return column ? { type: 'columnHeader', key: column.key, header: (column.header ?? []).map((c) => stripInline(c, mode)) } : undefined;
        }
        case 'caption':
            return owner.type === 'table' && owner.caption
                ? { type: 'caption', caption: (owner.caption ?? []).map((c) => stripInline(c, mode)) }
                : undefined;
    }
};
/** Fingerprint of a part of a node as compiled; `null` when the part does not exist. */
export function fingerprintPart(owner, part) {
    const shape = partShape(owner, part, 'f2');
    return shape === undefined ? null : FINGERPRINT_PREFIX + hashString(canonicalJson(shape));
}
/** `fingerprintMatches` for a part (`f1:` recomputed with value text). */
export function partFingerprintMatches(owner, part, stored) {
    const mode = stored.startsWith(LEGACY_PREFIX) ? 'f1' : 'f2';
    const shape = partShape(owner, part, mode);
    if (shape === undefined)
        return false;
    return stored === (mode === 'f1' ? LEGACY_PREFIX : FINGERPRINT_PREFIX) + hashString(canonicalJson(shape));
}
/** Fingerprint of a detail-list row object (e.g. one an insert created). */
export const fingerprintRow = (item) => FINGERPRINT_PREFIX + hashString(canonicalJson({ type: 'detailRow', ...stripRow(item, 'f2') }));
/** `fingerprintMatches` for a whole block instance. */
export function instanceFingerprintMatches(nodes, stored) {
    if (stored.startsWith(LEGACY_PREFIX))
        return stored === LEGACY_PREFIX + instanceHash(nodes);
    return stored === fingerprintInstance(nodes);
}
/**
 * Deep content fingerprint of a node list (sections included in full) —
 * what block versioning compares as a block's text (ADR-0103). Same
 * normalisation as `f2` (values as `{{key}}`, no assembly output, no locks).
 */
export const fingerprintContent = (nodes) => FINGERPRINT_PREFIX + hashString(canonicalJson(stripList(nodes, 'f2')));
//# sourceMappingURL=fingerprint.js.map